import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Eye,
  AlertTriangle,
  RefreshCw,
  Info,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { agrupacionesAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/DataTable.css';
import '../../styles/Agrupaciones.css';

export default function SinVinculacion({ tipo: propTipo }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Detect tipo from prop or path (/grupos/sin-vinculacion vs /colocadoras/sin-vinculacion)
  const isColocadora = propTipo === 'COLOCADORA' || location.pathname.includes('colocadora');
  const tipoModalidad = isColocadora ? 'COLOCADORA' : 'GRUPAL';
  const parentPath = isColocadora ? '/colocadoras' : '/grupos';

  const [data, setData] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [q, setQ] = useState('');
  const [conSaldo, setConSaldo] = useState('TODOS');
  const [ordenar, setOrdenar] = useState('FOLIO_ASC');

  const cargarSinVinculacion = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        tipoCredito: tipoModalidad,
        page: pagination.page,
        limit: pagination.limit,
        ordenar
      };
      if (q.trim()) params.q = q.trim();
      if (conSaldo === 'CON_SALDO') params.conSaldo = true;
      if (conSaldo === 'SIN_SALDO') params.conSaldo = false;

      const res = await agrupacionesAPI.sinVinculacion(params);
      setData(res.data.data || []);
      setResumen(res.data.resumen || null);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination?.total || 0,
        totalPages: res.data.pagination?.totalPages || 1
      }));
    } catch (err) {
      setError(describirError(err).mensaje);
    } finally {
      setLoading(false);
    }
  }, [tipoModalidad, pagination.page, pagination.limit, q, conSaldo, ordenar]);

  useEffect(() => {
    cargarSinVinculacion();
  }, [cargarSinVinculacion]);

  return (
    <div className="cartera-container">
      {/* Botón Volver */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(parentPath)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <ArrowLeft size={14} />
          <span>Volver a {isColocadora ? 'Colocadoras' : 'Grupos'}</span>
        </button>
      </div>

      {/* Encabezado */}
      <div className="cartera-header">
        <div>
          <div className="cartera-badge-env" style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>
            <span className="cartera-badge-dot" style={{ backgroundColor: '#d97706' }} />
            <span>EXCEPCIÓN HISTÓRICA · SIN VINCULACIÓN</span>
          </div>
          <h1 className="cartera-title">
            Créditos {isColocadora ? 'Colocadora' : 'Grupales'} sin Agrupación
          </h1>
          <p className="cartera-subtitle">
            Créditos con modalidad {tipoModalidad} migrados sin un identificador de agrupación activa asignado.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cargarSinVinculacion}
            disabled={loading}
            title="Actualizar datos"
          >
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Alerta Informativa */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '0.85rem 1rem',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '0.5rem',
        marginBottom: '1.25rem',
        color: '#1e40af',
        fontSize: '0.875rem'
      }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <strong>Consulta de auditoría y trazabilidad histórica:</strong> Estos registros no pertenecen a ninguna de las 
          {isColocadora ? ' 22 colocadoras operativas reales' : ' 10 agrupaciones operativas reales'}. 
          Sus saldos y vencimientos son plenamente calculados con autoridad canónica (*opening-state-aware*), 
          y no alteran los balances de las entidades vigentes.
        </div>
      </div>

      {/* KPI Cards */}
      <div className="cartera-kpi-grid">
        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">TOTAL REGISTROS</div>
          <div className="cartera-kpi-value font-mono">
            {resumen ? resumen.totalCreditos : '—'}
          </div>
          <div className="cartera-kpi-sub">Créditos sin agrupación</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO ACTUAL</div>
          <div className="cartera-kpi-value font-mono text-primary">
            {resumen ? formatoMoneda(resumen.saldoActual) : '—'}
          </div>
          <div className="cartera-kpi-sub">Canónico consolidado</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO VENCIDO</div>
          <div className="cartera-kpi-value font-mono text-danger">
            {resumen ? formatoMoneda(resumen.saldoVencido) : '—'}
          </div>
          <div className="cartera-kpi-sub">Exigible acumulado</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO POR VENCER</div>
          <div className="cartera-kpi-value font-mono text-muted">
            {resumen ? formatoMoneda(resumen.saldoPorVencer) : '—'}
          </div>
          <div className="cartera-kpi-sub">Vigente</div>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="cartera-filters-card">
        <div className="cartera-filters-row">
          <div className="cartera-search-box">
            <Search size={16} className="cartera-search-icon" />
            <input
              type="text"
              className="cartera-search-input"
              placeholder="Buscar por folio, cliente o referencia legacy..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Universo:</span>
            <select
              className="cartera-select"
              value={conSaldo}
              onChange={(e) => {
                setConSaldo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="TODOS">Todos los créditos</option>
              <option value="CON_SALDO">Con saldo vivo</option>
              <option value="SIN_SALDO">Saldo $0.00</option>
            </select>
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Ordenar:</span>
            <select
              className="cartera-select"
              value={ordenar}
              onChange={(e) => setOrdenar(e.target.value)}
            >
              <option value="FOLIO_ASC">Folio (A &rarr; Z)</option>
              <option value="FOLIO_DESC">Folio (Z &rarr; A)</option>
              <option value="SALDO_DESC">Mayor Saldo</option>
              <option value="SALDO_ASC">Menor Saldo</option>
              <option value="ATRASO_DESC">Mayor Atraso</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="cartera-error-card">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-secondary" onClick={cargarSinVinculacion}>
            Reintentar
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>FOLIO</th>
              <th>CLIENTE</th>
              <th>NOMBRE LEGACY</th>
              <th>PRODUCTO</th>
              <th>FRECUENCIA</th>
              <th className="text-right">SALDO ACTUAL</th>
              <th className="text-right">SALDO VENCIDO</th>
              <th className="text-center">ATRASO</th>
              <th>FECHA</th>
              <th className="text-right">ACCIÓN</th>
            </tr>
          </thead>
          <tbody>
            {loading && data.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <RefreshCw size={16} className="spin" />
                    <span>Cargando créditos sin vinculación...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-4 text-muted">
                  No se encontraron créditos sin vinculación que coincidan con los filtros.
                </td>
              </tr>
            ) : (
              data.map((credito) => (
                <tr
                  key={credito.id}
                  className="fila-clicable"
                  onClick={() => navigate(`/creditos/${credito.id}`)}
                  title={`Ver expediente del crédito ${credito.folio}`}
                >
                  <td>
                    <span className="font-mono text-bold creditos-folio-cell">
                      {credito.folio}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div className="font-semibold">{credito.cliente?.nombre || '—'}</div>
                      <div className="text-xs text-muted font-mono">{credito.cliente?.folio}</div>
                    </div>
                  </td>
                  <td>
                    <span className="text-sm text-muted">
                      {credito.nombreGrupoLegacy}
                    </span>
                  </td>
                  <td>
                    <span className="text-sm">{credito.productoNombre}</span>
                  </td>
                  <td>
                    <span className="text-sm">{credito.frecuencia}</span>
                  </td>
                  <td className="text-right font-mono font-semibold">
                    {formatoMoneda(credito.saldoActual)}
                  </td>
                  <td className="text-right font-mono font-semibold text-danger">
                    {formatoMoneda(credito.saldoVencido)}
                  </td>
                  <td className="text-center">
                    {credito.diasAtraso > 0 ? (
                      <span className="badge badge-danger font-mono font-semibold">
                        {credito.diasAtraso} d
                      </span>
                    ) : (
                      <span className="badge badge-success font-mono">0 d</span>
                    )}
                  </td>
                  <td className="text-sm text-muted">
                    {credito.fechaDesembolso ? new Date(credito.fechaDesembolso).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      onClick={() => navigate(`/creditos/${credito.id}`)}
                      title="Ver crédito"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="cartera-pagination">
        <span className="text-sm text-muted">
          Mostrando {data.length} de {pagination.total} créditos sin vinculación
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page <= 1 || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <ChevronLeft size={14} />
            <span>Anterior</span>
          </button>
          <span className="text-sm font-mono px-2">
            Pág. {pagination.page} de {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <span>Siguiente</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
